// Confirm order action with modal

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DialogTitle } from '@headlessui/react';

import { logError } from 'services/logging/logging';
import { useConfirmOrder } from 'services/repositories/transactions/transactions';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { EMPTY_FUNCTION } from 'support/helpers/const/const';
import { formatCalendarDay } from 'support/helpers/dateTime/dateTime';
import { routeToCreatePage, routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { useOCRAvailability } from 'support/helpers/ocr/ocr';
import { DocumentType, DocumentTypeWithVirtual, MessageV2DTO, TransactionState } from 'support/types';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button';
import { Translate } from 'components/Translate/Translate';

import { ActionConfig } from '../components/types';
import { useContainsSearchQueryAction, useDocumentActionWithOCR } from './helpers';

// Custom hook for creating confirm order action with modal
const CONFIRM_ENTIRE_ORDER_SEARCH_ACTION = 'confirm_order';
const CONFIRM_PARTIAL_ORDER_URL_ACTION = 'partially_confirm_order';
export const useConfirmOrderAction = (
  message: MessageV2DTO,
  documentType: DocumentType,
  relatedTransactions: Array<MessageV2DTO> | undefined,
  ocrAvailability: ReturnType<typeof useOCRAvailability>,
  baseConfig: Omit<ActionConfig, 'onClick' | 'documentTypeExists'>,
): ActionConfig => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const openConfirmEntireOrderModalByDefault = useContainsSearchQueryAction(CONFIRM_ENTIRE_ORDER_SEARCH_ACTION);
  const partialConfirmOrderByDefault = useContainsSearchQueryAction(CONFIRM_PARTIAL_ORDER_URL_ACTION);
  const [isConfirmEntireOrderModalOpen, setIsConfirmEntireOrderModalOpen] = useState(
    openConfirmEntireOrderModalByDefault,
  );

  const { mutate: confirmOrder, isLoading: isConfirming } = useConfirmOrder();

  const confirmPartially = useCallback(() => {
    navigate(routeToCreatePage(message.id, DocumentTypeWithVirtual.partialOrderConfirmation));
  }, [message.id, navigate]);

  useEffect(() => {
    if (partialConfirmOrderByDefault) {
      confirmPartially();
    }
  }, [confirmPartially, partialConfirmOrderByDefault]);

  const testOrderResponseAction = () => {
    const result = !relatedTransactions?.some((message: MessageV2DTO) => {
      return (
        (message.type === DocumentType.shippingNotice || message.type === DocumentType.invoice) &&
        [TransactionState.success, TransactionState.pending].includes(message.state as TransactionState)
      );
    });
    return result;
  };

  const handleConfirmOrder = () => {
    confirmOrder(
      { messageId: message.id },
      {
        onSuccess: ({ transactionId, transactionType }) => {
          setIsConfirmEntireOrderModalOpen(false);
          navigate(routeToTransactionPage(transactionType, transactionId));
        },
        onError: (e) => {
          logError(e);
          addNotification(
            {
              title: t(`webedi:notifications.confirmOrderFailed.title`),
              subtitle: t(`webedi:notifications.confirmOrderFailed.subtitle`),
            },
            NotificationType.error,
          );
        },
      },
    );
  };

  const baseAction = useDocumentActionWithOCR(message, documentType, relatedTransactions, ocrAvailability, {
    ...baseConfig,
    test: testOrderResponseAction,
  });

  return {
    ...baseAction,
    label: t('transactions:show.actionButtons.createOrderResponse'),
    menuTitle: t('transactions:show.actionButtons.confirmOrder'),
    analyticsId: 'transactions:create_order_response',
    subActions: [
      {
        analyticsId: 'transactions:confirm_order_fully',
        label: t('transactions:show.actionButtons.confirmOrderFully'),
        onClick: () => setIsConfirmEntireOrderModalOpen(true),
      },
      {
        analyticsId: 'transactions:confirm_order_partially',
        label: t('transactions:show.actionButtons.confirmPartially'),
        onClick: confirmPartially,
      },
    ],
    renderModal: () => (
      <>
        {baseAction.renderModal?.(message)}
        <ConfirmEntireOrderModal
          message={message}
          isLoading={isConfirming}
          open={isConfirmEntireOrderModalOpen}
          setOpen={setIsConfirmEntireOrderModalOpen}
          onConfirm={handleConfirmOrder}
        />
      </>
    ),
  };
};

// Define the PartialOrderCanonical type
type PartialOrderCanonical = {
  line_items: Array<{ ordered_quantity: number }> | undefined;
  requested_delivery_date: number | undefined;
};

function ConfirmEntireOrderModal({
  open,
  setOpen,
  message,
  onConfirm,
  isLoading,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  message: MessageV2DTO;
  onConfirm: () => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();

  const handleConfirm = () => {
    onConfirm();
  };

  const canonical = message?.canonical as PartialOrderCanonical;
  const lineItemsLength = canonical?.line_items?.length || 0;
  const totalOrderedQuantity = canonical?.line_items?.reduce((acc, item) => acc + item.ordered_quantity, 0) || 0;
  const confirmedDeliveryDate = formatCalendarDay(canonical?.requested_delivery_date);
  const orderSenderPartner = message?.sender_partner;

  return (
    <BasePopup open={open} setOpen={isLoading ? EMPTY_FUNCTION : setOpen}>
      <div className="p-6">
        <DialogTitle as="h3" className="text-lg font-medium text-gray-900">
          {t('webedi:confirm_entire_order.modal.title')}
        </DialogTitle>
        <p className="text-sm text-gray-500">
          {t('webedi:confirm_entire_order.modal.description', { partner: orderSenderPartner?.name })}
        </p>
        <div className="my-4 flex flex-col gap-4 rounded-md bg-gray-50 p-4">
          <p className="flex items-center justify-between text-sm text-gray-500">
            <span>{t('webedi:confirm_entire_order.modal.total_ordered_quantity')}</span>
            <span className="text-black">{totalOrderedQuantity}</span>
          </p>
          <p className="flex items-center justify-between text-sm text-gray-500">
            <span>{t('webedi:confirm_entire_order.modal.line_items_count')}</span>
            <span className="text-black">{lineItemsLength}</span>
          </p>
          <p className="flex items-center justify-between text-sm text-gray-500">
            <span>{t('webedi:confirm_entire_order.modal.confirmed_delivery_date')}</span>
            <span className="text-black">{confirmedDeliveryDate}</span>
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            analyticsId="webedi:confirm_entire_order_cancel"
            variant="secondary"
            onClick={() => setOpen(false)}
            disabled={isLoading}
          >
            <Translate i18nKey="common:close" />
          </Button>
          <Button
            analyticsId="webedi:confirm_entire_order_confirm"
            variant="primary"
            onClick={handleConfirm}
            loading={isLoading}
          >
            <Translate i18nKey="webedi:confirm_entire_order.modal.cta" />
          </Button>
        </div>
      </div>
    </BasePopup>
  );
}
